import { Alert, Box, Button, Paper, Stack, TextField, Typography } from '@mui/material';
import { useState, type FormEvent } from 'react';
import { config } from '@/config/env';
import { useAuth } from '@/lib/auth/AuthContext';
import type { AuthProvider } from '@/lib/auth/types';

/**
 * JEDEN FORMULARZ. JEDNO HASŁO. JEDNA SESJA.
 *
 * ┌─ CO ZNIKŁO I DLACZEGO TO NIE JEST KOSMETYKA ────────────────────────────────────────────────┐
 * │ Poprzednia wersja renderowała formularz NA KAŻDY skonfigurowany realm i miała gałęzie       │
 * │ „opcjonalne / Podłącz / Odłącz". Opisywała architekturę z dwoma katalogami kont: operatorów │
 * │ CRM i administratorów Drive. Ta architektura już nie istnieje — do źródeł chodzi backend    │
 * │ własną tożsamością serwisową — ale RUSZTOWANIE po niej zostało, a rusztowanie jest          │
 * │ zaproszeniem do powrotu: wystarczyłby jeden dodany provider, żeby na ekranie właściciela    │
 * │ znów pojawiło się drugie pole hasła.                                                        │
 * │                                                                                              │
 * │ Dlatego „jedno logowanie" jest tu WŁASNOŚCIĄ KOMPONENTU, a nie tylko asercją w teście.      │
 * └──────────────────────────────────────────────────────────────────────────────────────────────┘
 */
function FormularzLogowania({ provider }: { provider: AuthProvider }) {
  const auth = useAuth();
  const state = auth.realms[provider.realm];
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    void auth.signIn(provider.realm, { login, password });
  }

  return (
    <Box component="form" onSubmit={onSubmit} sx={{ py: 1 }}>
      {state?.error ? (
        <Alert severity="error" sx={{ mb: 2 }} role="alert">
          {state.error}
        </Alert>
      ) : null}

      <Stack spacing={2}>
        {/* Tożsamość Control Center to adres e-mail. Etykieta zależna od systemu straciła sens
            razem z drugim realmem — nie ma już „loginu operatora CRM" obok „e-maila
            administratora Drive". */}
        <TextField
          label="Adres e-mail"
          value={login}
          onChange={(e) => setLogin(e.target.value)}
          autoComplete="username"
          size="small"
          fullWidth
          required
        />
        <TextField
          label="Hasło"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="current-password"
          size="small"
          fullWidth
          required
        />
        <Button type="submit" variant="contained" disabled={state?.pending}>
          {state?.pending ? 'Logowanie…' : 'Zaloguj'}
        </Button>
      </Stack>
    </Box>
  );
}

export function LoginPage() {
  const auth = useAuth();

  // Jeden dostawca to warunek, nie założenie — dlatego wyciągamy go tak, żeby typ mówił
  // prawdę: przy innej liczbie dostawców po prostu go nie ma i renderuje się komunikat błędu.
  const jedyny = auth.providers.length === 1 ? auth.providers[0] : undefined;

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', pt: 6 }}>
      <Paper variant="outlined" sx={{ p: 4, width: 460, maxWidth: '100%' }}>
        <Typography variant="h5" gutterBottom>
          Control Center
        </Typography>
        {/*
          TEKST MUSI NADĄŻAĆ ZA ARCHITEKTURĄ. Poprzednia wersja opisywała dwie sesje i token
          żyjący w pamięci karty — oba zdania przestały być prawdziwe razem z przejściem na jedno
          logowanie. Nieaktualny opis właściwości bezpieczeństwa jest gorszy niż jego brak: brzmi
          jak obietnica i bywa czytany jak gwarancja.
        */}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Jedno logowanie. Control Center sięga do CRM-u i do systemu kierowców po stronie serwera,
          własną tożsamością serwisową — nie prosi o hasła do tych systemów i nigdy ich nie widzi.
          Sesja żyje w ciasteczku <code>HttpOnly</code>, którego skrypt strony nie odczyta, więc
          przetrwa odświeżenie karty.
        </Typography>

        {config.environment === 'unknown' ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            Nie ustawiono zmiennej <code>VITE_CC_ENV</code> — aplikacja nie wie, do jakiego środowiska
            jest podłączona.
          </Alert>
        ) : null}

        {/*
          DRUGI DOSTAWCA TOŻSAMOŚCI TO BŁĄD KONFIGURACJI, A NIE DRUGI FORMULARZ.
          Renderowanie listy dostawców „na wszelki wypadek" jest właśnie tym mechanizmem, który
          przywróciłby dwa hasła na ekranie właściciela. Jeżeli konfiguracja kiedykolwiek
          przestanie mieć dokładnie jednego dostawcę, ekran ma to POWIEDZIEĆ — cicho pokazany
          drugi formularz byłby regresją, której nikt nie zauważy, dopóki nie zobaczy jej OWNER.
        */}
        {jedyny ? (
          <FormularzLogowania provider={jedyny} />
        ) : (
          <Alert severity="error" role="alert">
            Błąd konfiguracji: Control Center ma dokładnie jedno logowanie, a skonfigurowano
            dostawców tożsamości: {auth.providers.length}. Zaloguj się dopiero po naprawieniu
            konfiguracji — ekran z dwoma hasłami oznaczałby powrót do architektury, której już nie ma.
          </Alert>
        )}

        <Alert severity="info" sx={{ mt: 3 }}>
          Zakres tego, co zobaczysz, wynika z roli Twojego konta i jest egzekwowany po stronie
          serwera przy każdym żądaniu — ukrycie ekranu nigdy nie jest tu autoryzacją.
        </Alert>
      </Paper>
    </Box>
  );
}
